import { forwardRef, memo, useMemo } from 'react';
import type Konva from 'konva';
import { Group, Image, Rect, Text } from 'react-konva';
import useImage from 'use-image';
import type { Node } from '../../../core/models/scene';
import { useTranslation } from '../../../hooks/useTranslation';
import { MEDIA_CANVAS_TEST_IDS } from '../../media/constants/mediaCanvasTestIds';
import { resolveMediaSrc } from '../../media/utils/resolveMediaSrc';
import { clampNormalizedCropRect, computeImageLayout } from '../utils/imageShapeLayout';

type KonvaNodeEvent = Konva.KonvaEventObject<MouseEvent | TouchEvent | DragEvent | Event>;

interface ImageShapeNodeProps {
  node: Node;
  selected: boolean;
  diagramId?: string;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: KonvaNodeEvent) => void;
  onDoubleClick?: (e: KonvaNodeEvent) => void;
  onTransformStart?: (e: KonvaNodeEvent) => void;
  onTransformEnd?: (e: KonvaNodeEvent) => void;
  canWrite?: boolean;
}

const BaseImageShapeNode = forwardRef<Konva.Group, ImageShapeNodeProps>(
  ({ node, diagramId, onDragStart, onDragMove, onDragEnd, onClick, onDoubleClick, onTransformStart, onTransformEnd, canWrite = true }, ref) => {
    const { t } = useTranslation('diagram');
    const width = (node.width ?? node.w ?? 240) as number;
    const height = (node.height ?? node.h ?? 160) as number;
    const attrs = (node.attrs as Record<string, unknown>) ?? {};
    const objectKey =
      typeof attrs.objectKey === 'string'
        ? attrs.objectKey
        : typeof attrs.photoRef === 'string'
          ? attrs.photoRef
          : '';
    const uploadState = attrs.uploadState === 'uploading' ? 'uploading' : 'ready';
    const isUploadPlaceholder = uploadState === 'uploading' || objectKey.trim().length === 0;
    const fit = attrs.fit === 'contain' ? 'contain' : 'cover';
    const opacity = Math.max(0, Math.min(100, Number(typeof attrs.opacity === 'number' ? attrs.opacity : 100))) / 100;
    const borderEnabled = typeof attrs.borderEnabled === 'boolean' ? attrs.borderEnabled : true;
    const borderWidth = Math.max(0, Number(typeof attrs.borderWidth === 'number' ? attrs.borderWidth : 1));
    const borderColor = typeof attrs.borderColor === 'string' ? attrs.borderColor : '#CBD5E1';
    const borderDash = Array.isArray(attrs.borderDash)
      ? (attrs.borderDash as number[])
      : Array.isArray(attrs.dash)
        ? (attrs.dash as number[])
        : [];
    const borderDashEnabled = Boolean(
      (typeof attrs.dashEnabled === 'boolean' ? attrs.dashEnabled : borderDash.length > 0) &&
      borderDash.length > 0,
    );
    const caption = typeof attrs.caption === 'string' ? attrs.caption : '';
    const cropRect = clampNormalizedCropRect((attrs.crop as Record<string, unknown> | undefined) ?? null);
    const statusLabel = useMemo(() => {
      if (uploadState === 'uploading') {
        return t('mediaUpload.shapeStatus.uploading');
      }
      return t('mediaUpload.shapeStatus.placeholder');
    }, [t, uploadState]);
    const src = useMemo(
      () => resolveMediaSrc(objectKey, { diagramId }),
      [objectKey, diagramId],
    );
    const [image] = useImage(src, 'anonymous');
    const imageLayout = useMemo(() => {
      if (!image) return null;
      return computeImageLayout({
        width,
        height,
        fit,
        sourceWidth: image.width || 1,
        sourceHeight: image.height || 1,
        crop: cropRect,
      });
    }, [cropRect, fit, height, image, width]);

    return (
      <Group
        ref={ref}
        id={node.id}
        name="shape"
        data-testid={isUploadPlaceholder ? MEDIA_CANVAS_TEST_IDS.placeholder : MEDIA_CANVAS_TEST_IDS.imageNode}
        data-media-upload-state={isUploadPlaceholder ? 'uploading' : 'ready'}
        x={node.x || 0}
        y={node.y || 0}
        rotation={typeof (node as { rotation?: number }).rotation === 'number' ? (node as { rotation?: number }).rotation : 0}
        draggable={canWrite}
        onClick={onClick}
        onDblClick={onDoubleClick}
        onDragStart={(e) => onDragStart?.({ x: e.target.x(), y: e.target.y() })}
        onDragMove={(e) => onDragMove?.({ x: e.target.x(), y: e.target.y() })}
        onDragEnd={(e) => onDragEnd?.({ x: e.target.x(), y: e.target.y() })}
        onTransformStart={onTransformStart}
        onTransformEnd={onTransformEnd}
      >
        <Rect
          x={0}
          y={0}
          width={width}
          height={height}
          cornerRadius={8}
          fill="#F8FAFC"
          stroke={borderEnabled ? borderColor : 'transparent'}
          strokeWidth={borderEnabled ? borderWidth : 0}
          dash={borderEnabled && borderDashEnabled ? borderDash : undefined}
          dashEnabled={borderEnabled && borderDashEnabled}
        />
        {image && uploadState === 'ready' ? (
          <Image
            image={image}
            x={imageLayout?.x ?? 0}
            y={imageLayout?.y ?? 0}
            width={imageLayout?.width ?? width}
            height={imageLayout?.height ?? height}
            cornerRadius={8}
            crop={imageLayout?.crop}
            opacity={opacity}
            listening={false}
          />
        ) : (
          <Text
            data-testid={MEDIA_CANVAS_TEST_IDS.placeholder}
            x={0}
            y={0}
            width={width}
            height={height}
            text={statusLabel}
            align="center"
            verticalAlign="middle"
            fontSize={14}
            fill="#64748B"
            listening={false}
          />
        )}
        {caption ? (
          <>
            <Rect
              x={0}
              y={Math.max(0, height - 24)}
              width={width}
              height={24}
              cornerRadius={[0, 0, 8, 8]}
              fill="rgba(15,23,42,0.65)"
              listening={false}
            />
            <Text
              x={8}
              y={Math.max(0, height - 18)}
              width={Math.max(0, width - 16)}
              height={16}
              text={caption}
              fontSize={12}
              fill="#FFFFFF"
              listening={false}
              ellipsis
            />
          </>
        ) : null}
      </Group>
    );
  },
);

BaseImageShapeNode.displayName = 'ImageShapeNode';

export const ImageShapeNode = memo(BaseImageShapeNode);
